<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ClipboardIcon, CheckIcon, DocumentTextIcon } from '@heroicons/vue/solid';
import { NumberService, WhatsappCodeService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { useUiStore } from '@/stores';
import { NumberModel, WhatsappCodeModel } from '@/types';
import { applyPhoneMask } from '@/utils/number';
import { moment } from '@/utils/datetime';
import SVGWhatsApp from '@/components/svg/SVGWhatsApp.vue';

const route = useRoute();
const { t } = useI18n();
const uiStore = useUiStore();

const numberId = parseInt(route.params.id as string, 10);
const number = ref<NumberModel | null>(null);
const codes = ref<WhatsappCodeModel[]>([]);
const latestCode = ref<WhatsappCodeModel | null>(null);

const count = ref(0);
const perPage = 10;
const currentPage = ref(1);
const latestCodeVisibleSeconds = 180;

const copied = ref(false);
const newlyReceivedId = ref<number | null>(null);
let pollingInterval: number | null = null;

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const digitChars = computed(() => {
    if (!latestCode.value || !latestCode.value.code) {
        return Array(6).fill('-');
    }
    const codeStr = latestCode.value.code.toString();
    const chars = codeStr.split('');
    while (chars.length < 6) {
        chars.push('-');
    }
    return chars;
});

const formatTime = (dateStr: string): string => {
    return moment(dateStr).format('DD/MM/YYYY HH:mm:ss');
};

const isRecentlyReceived = (code: WhatsappCodeModel): boolean => {
    const receivedAt = moment(code.receivedAt);
    if (!receivedAt.isValid()) return false;

    const secondsSinceReceived = moment().diff(receivedAt, 'seconds');
    return secondsSinceReceived >= 0 && secondsSinceReceived <= latestCodeVisibleSeconds;
};

const setLatestCode = (code?: WhatsappCodeModel) => {
    latestCode.value = code && isRecentlyReceived(code) ? code : null;
};

const copyToClipboard = async () => {
    if (!latestCode.value) return;
    try {
        await navigator.clipboard.writeText(latestCode.value.code);
        copied.value = true;
        toast.success(t('whatsappCode.toast.copiedSuccess'));
        setTimeout(() => {
            copied.value = false;
        }, 1500);
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to copy: ', err);
    }
};

const fetchNumber = async () => {
    try {
        const { data } = await NumberService.get(numberId);
        number.value = data;
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchCodes = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
        const { data, headers } = await WhatsappCodeService.getCodes(numberId, {
            params: {
                page: currentPage.value,
                per_page: perPage,
            },
        });
        codes.value = data;
        count.value = parseInt(headers['x-pagination-count']) || data.length;

        if (data.length > 0 && currentPage.value === 1) {
            setLatestCode(data[0]);
        } else if (!latestCode.value && data.length > 0) {
            // fallback if we are on page 2+ and didn't load first page yet
            const firstPageRes = await WhatsappCodeService.getCodes(numberId, {
                params: { page: 1, per_page: 1 },
            });
            if (firstPageRes.data.length > 0) {
                setLatestCode(firstPageRes.data[0]);
            }
        } else if (data.length === 0 && currentPage.value === 1) {
            setLatestCode();
        }
    } catch (error) {
        toastServiceError(error);
    } finally {
        if (showLoading) setLoading(false);
    }
};

const startPolling = () => {
    pollingInterval = window.setInterval(async () => {
        try {
            // Check for new codes on page 1
            const { data } = await WhatsappCodeService.getCodes(numberId, {
                params: {
                    page: 1,
                    per_page: 1,
                },
            });

            if (data && data.length > 0) {
                const newest = data[0];
                const previousLatestId = latestCode.value?.id || null;
                setLatestCode(newest);
                const isNew = Boolean(latestCode.value && newest.id !== previousLatestId);

                if (isNew) {
                    if (currentPage.value === 1) {
                        // Prepend if not already in current codes list
                        if (!codes.value.some((c) => c.id === newest.id)) {
                            codes.value.unshift(newest);
                            if (codes.value.length > perPage) {
                                codes.value.pop();
                            }
                            // Trigger highlight animation
                            newlyReceivedId.value = newest.id;
                            setTimeout(() => {
                                newlyReceivedId.value = null;
                            }, 2500);
                        }
                    } else {
                        // If user is on page 2+, just refresh current page
                        await fetchCodes(false);
                    }
                }
            } else {
                setLatestCode();
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Polling error:', err);
        }
    }, 5000);
};

const handlePageChange = (page: number) => {
    currentPage.value = page;
    fetchCodes(true);
};

onMounted(async () => {
    await fetchNumber();
    await fetchCodes(true);
    startPolling();
});

onUnmounted(() => {
    if (pollingInterval) {
        window.clearInterval(pollingInterval);
    }
});
</script>

<template>
    <div class="space-y-6 p-4 sm:p-6 lg:p-8">
        <UiPageHeader>
            <template #info>
                <div class="flex items-center space-x-2">
                    <UiBackButton :fallback-to="{ name: 'ListWhatsappCodeNumbers' }" />
                    <h1 class="flex items-center text-xl font-semibold text-gray-800">
                        <SVGWhatsApp class="mr-1.5 h-6 w-6 text-[#25D366]" />
                        {{
                            number
                                ? $t('whatsappCode.monitoringNumber', {
                                      number: applyPhoneMask(number.number),
                                  })
                                : $t('whatsappCode.title')
                        }}
                    </h1>
                </div>
            </template>
        </UiPageHeader>

        <!-- Premium Display for Latest Active Code -->
        <UiPanel
            class="mx-auto flex max-w-xl flex-col items-center overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-900 to-indigo-950 p-6 text-white shadow-lg sm:p-8"
        >
            <span
                class="mb-4 text-xs font-semibold uppercase tracking-widest text-indigo-200 sm:text-sm"
            >
                {{ $t('whatsappCode.lastReceivedCode') }}
            </span>

            <div v-if="latestCode" class="flex w-full flex-col items-center">
                <!-- Digit Boxes -->
                <div class="mb-6 flex justify-center space-x-2 sm:space-x-3">
                    <div
                        v-for="(char, idx) in digitChars"
                        :key="`digit-${idx}`"
                        class="flex h-14 w-11 transform items-center justify-center rounded-xl border border-white/20 bg-white/10 font-mono text-2xl font-extrabold text-white shadow-inner backdrop-blur-md transition-transform duration-150 hover:scale-105 sm:h-20 sm:w-14 sm:text-4xl"
                    >
                        {{ char }}
                    </div>
                </div>

                <div class="flex flex-col items-center space-y-3">
                    <button
                        class="flex items-center space-x-2 rounded-lg px-6 py-2 text-sm font-medium shadow-md transition-all duration-150 focus:outline-none"
                        :class="[
                            copied
                                ? 'bg-green-600 text-white hover:bg-green-700'
                                : 'bg-indigo-600 text-white hover:bg-indigo-700',
                        ]"
                        @click="copyToClipboard"
                    >
                        <ClipboardIcon v-if="!copied" class="mr-1 h-4 w-4" />
                        <CheckIcon v-else class="mr-1 h-4 w-4 text-green-300" />
                        <span>{{
                            copied ? $t('whatsappCode.copied') : $t('whatsappCode.copyCode')
                        }}</span>
                    </button>

                    <span class="text-xs font-light text-indigo-300">
                        {{ $t('whatsappCode.table.receivedAt') }}:
                        {{ formatTime(latestCode.receivedAt) }}
                    </span>
                </div>
            </div>

            <!-- Empty State for Monitor -->
            <div v-else class="flex flex-col items-center py-6 text-center text-indigo-300">
                <SVGWhatsApp class="mb-3 h-10 w-10 animate-pulse text-[#25D366]" />
                <p class="text-sm">{{ $t('whatsappCode.noCodesReceived') }}</p>
            </div>
        </UiPanel>

        <!-- Chronological Code History List -->
        <div class="space-y-4">
            <h2 class="flex items-center text-lg font-bold text-gray-800">
                <DocumentTextIcon class="mr-1 h-5 w-5 text-gray-500" />
                {{ $t('whatsappCode.history') }}
            </h2>

            <div class="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                                {{ $t('whatsappCode.table.code') }}
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                                {{ $t('whatsappCode.table.receivedAt') }}
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                                {{ $t('whatsappCode.table.transcript') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200 bg-white">
                        <tr
                            v-for="code in codes"
                            :key="code.id"
                            :class="[
                                newlyReceivedId === code.id
                                    ? 'animate-new-row'
                                    : 'hover:bg-gray-50/50',
                            ]"
                            class="transition-colors duration-150"
                        >
                            <td
                                class="whitespace-nowrap px-6 py-4 font-mono text-sm font-bold text-indigo-600"
                            >
                                {{ code.code }}
                            </td>
                            <td class="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                {{ formatTime(code.receivedAt) }}
                            </td>
                            <td class="max-w-lg break-words px-6 py-4 text-sm text-gray-600">
                                {{ code.transcript || '-' }}
                            </td>
                        </tr>
                        <tr v-if="codes.length === 0">
                            <td
                                colspan="3"
                                class="px-6 py-8 text-center text-sm italic text-gray-500"
                            >
                                {{ $t('core.messages.noRecordsFound') }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <UiPagination
                v-if="codes.length"
                v-model="currentPage"
                :count="count"
                :perPage="perPage"
                @update:modelValue="handlePageChange"
            />
        </div>
    </div>
</template>

<style scoped>
@keyframes rowHighlight {
    0% {
        background-color: rgba(99, 102, 241, 0.2);
    }
    100% {
        background-color: transparent;
    }
}
.animate-new-row {
    animation: rowHighlight 2.5s ease-out;
}
</style>
